
import { useService } from "@/context/ServiceContext";
import { cn } from "@/lib/utils";
import { useEffect, useRef } from "react";
import { useIsMobile } from "@/hooks/use-mobile";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "./ui/button";

type ServiceItem = {
  id: "nutrition-counselling" | "gut-health" | "weight-management" | "sports-nutrition" | "chronic-diseases" | "lifecycle-nutrition";
  label: string;
};

const services: ServiceItem[] = [
  { id: "nutrition-counselling", label: "Nutrition Counselling" },
  { id: "gut-health", label: "Gut Health" },
  { id: "weight-management", label: "Weight Management" },
  { id: "sports-nutrition", label: "Sports Nutrition" },
  { id: "chronic-diseases", label: "Chronic Diseases" },
  { id: "lifecycle-nutrition", label: "Lifecycle Nutrition" }
];

export const ServiceNav = () => {
  const { activeService, scrollToService } = useService();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const activeButtonRef = useRef<HTMLButtonElement>(null);
  const isMobile = useIsMobile();

  // Enhanced scroll behavior with additional debugging information
  useEffect(() => {
    if (activeButtonRef.current && scrollContainerRef.current) {
      try {
        // Calculate the scroll position to center the active button
        const buttonRect = activeButtonRef.current.getBoundingClientRect();
        const containerRect = scrollContainerRef.current.getBoundingClientRect();
        
        const scrollLeft = 
          buttonRect.left + 
          scrollContainerRef.current.scrollLeft - 
          containerRect.left - 
          (containerRect.width / 2) + 
          (buttonRect.width / 2);
        
        console.log(`Scrolling nav to position: ${scrollLeft}px`);
        
        scrollContainerRef.current.scrollTo({
          left: Math.max(0, scrollLeft),
          behavior: 'smooth'
        });
      } catch (error) {
        console.error("Error scrolling service nav:", error);
      }
    }
  }, [activeService]);

  // Make scroll buttons more responsive
  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      const scrollAmount = isMobile ? -100 : -200;
      scrollContainerRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      const scrollAmount = isMobile ? 100 : 200;
      scrollContainerRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  const handleServiceClick = (serviceId: ServiceItem["id"]) => {
    // Force the scroll behavior to be immediate on direct click
    scrollToService(serviceId);
    
    // Log for debugging
    console.log(`Service clicked: ${serviceId}`);
  };

  return (
    <div className="bg-white sticky top-0 z-30 border-b shadow-sm">
      <div className="container mx-auto relative">
        {/* Show scroll buttons on all devices for better usability */}
        <Button 
          variant="outline" 
          size="icon" 
          className="absolute left-0 top-1/2 -translate-y-1/2 z-10 rounded-full shadow-md md:flex hidden"
          onClick={scrollLeft}
          aria-label="Scroll left"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <Button 
          variant="outline" 
          size="icon" 
          className="absolute right-0 top-1/2 -translate-y-1/2 z-10 rounded-full shadow-md md:flex hidden"
          onClick={scrollRight}
          aria-label="Scroll right"
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
        
        <div 
          ref={scrollContainerRef}
          className="overflow-x-auto whitespace-nowrap py-4 px-2 md:px-8 scrollbar-hide -webkit-overflow-scrolling-touch"
          role="tablist"
        >
          <div className="flex space-x-4 min-w-max">
            {services.map((service) => (
              <button
                key={service.id}
                ref={service.id === activeService ? activeButtonRef : null}
                onClick={() => handleServiceClick(service.id)}
                className={cn(
                  "px-4 py-2 rounded-full transition-colors text-sm md:text-base font-medium flex-shrink-0",
                  activeService === service.id
                    ? "bg-primary text-white"
                    : "text-primary hover:bg-primary/10"
                )}
                role="tab"
                aria-selected={activeService === service.id}
                aria-controls={service.id}
                id={`tab-${service.id}`}
              >
                {service.label}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
